import { InteractionDto, UserDto } from '@act/shared/data-transfer-objects';
import { ReactElement } from 'react';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { phoneFormatter } from 'utilities/formatters/phone-formatter';
import { timeFormatter } from '@act/shared/utilities';
import { useNavigateToMemberPage, useNavigateToCalls } from '../../app/routing/navigation-hooks';
import Activity from './activity';
import { Box } from '@mui/material';
import { IconAvatar } from '@act/storybook';

interface InteractionActivityProps {
  interaction: InteractionDto;
}

const InteractionActivity = ({ interaction }: InteractionActivityProps) => {
  const interactableId = interaction.subjectIds[0];
  const member: UserDto =
    membersAppSelectors.useSelectMemberByInteractableId(interactableId);
  const toMemberPage = useNavigateToMemberPage();
  const toCalls = useNavigateToCalls();

  // Check if this is a call without a member associated
  const isCallWithoutMember = interaction.interactionType === 'call' && !member;
  const humanTimestamp = timeFormatter.epochToHumanReadableTime(
    interaction.timestamp
  );
  const humanDuration = timeFormatter.getRoughDurationString(
    interaction.duration
  );
  let memberName: string = member?.name;
  let disabled: boolean = false;
  if (interaction.subjectIds.length === 0) {
    memberName = 'Could not find an associated member';
    if (interaction.interactionType === 'call') {
      if (interaction.incoming && interaction.originator) {
        memberName = phoneFormatter.stringToPretty(
          interaction.originator.phoneNumber
        );
      } else if (!interaction.incoming && interaction.receiver) {
        memberName = phoneFormatter.stringToPretty(
          interaction.receiver.phoneNumber
        );
      }
    }
    disabled = true;
  }
  const callback: () => void = () => {
    if (member && member.assignedGuideId && member.id) {
      toMemberPage(member.assignedGuideId, member.id);
    } else if (isCallWithoutMember) {
      toCalls({ onlyUnassignedCalls: true });
    }
  };

  let subtitle = `${memberName} • ${humanTimestamp}`;
  if (humanDuration) {
    subtitle += ` (${humanDuration})`;
  }

  let title: string;
  let icon: ReactElement;
  switch (interaction.interactionType) {
    case 'call':
      if (interaction.incoming) {
        title = 'Incoming call';
        icon = <IconAvatar avatarType="incoming_call" />;
      } else {
        title = 'Outgoing call';
        icon = <IconAvatar avatarType="outgoing_call" />;
      }
      break;
    case 'visit':
      title = 'Visit';
      icon = <IconAvatar avatarType="visit" />;
      break;
    case 'message':
      title = 'Message';
      icon = <IconAvatar avatarType="message" />;
      break;
    case 'note':
      title = 'Note';
      icon = <IconAvatar avatarType="note" />;
      break;
    default:
      title = 'Error - Unknown type';
      icon = <IconAvatar avatarType="error" />;
      break;
  }

  return (
    <Box
      onClick={callback}
      sx={{
        padding: '16px',
        '&:hover': {
          backgroundColor: 'rgba(25, 28, 29, 0.08)',
          cursor: 'pointer',
        },
      }}
    >
      <Activity
        title={title}
        subtitle={subtitle}
        icon={icon}
        callback={callback}
        disabled={disabled}
      />
    </Box>
  );
};

export default InteractionActivity;
